import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "upload-field" }
const _hoisted_2 = {
  key: 0,
  class: "image-preview"
}

import { computed, reactive, ref, watch } from 'vue';
import { ElMessage, type FormInstance, type FormRules } from 'element-plus';
import GoodsSpecEditor from './goods-spec-editor.vue';

interface GoodsSpec {
  name: string;
  options: string[];
}

interface GoodsEditorFormState {
  id?: number;
  categoryId?: number;
  image?: string;
  name: string;
  description: string;
  price: string;
  giftPoints: number;
  stock: number;
  sequence: number;
  isOnSale: boolean;
  isPointsExchange: boolean;
  specs: GoodsSpec[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'goods-editor-dialog',
  props: {
    visible: { type: Boolean },
    mode: {},
    initialValue: {},
    categoryOptions: {},
    uploadUrl: {},
    uploadHeaders: {},
    baseUrl: {},
    submitting: { type: Boolean }
  },
  emits: ["update:visible", "submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// eslint-disable-next-line func-call-spacing
const emit = __emit;

const formRef = ref<FormInstance>();

const createDefaultState = (): GoodsEditorFormState => ({
  id: undefined,
  categoryId: undefined,
  image: '',
  name: '',
  description: '',
  price: '',
  giftPoints: 0,
  stock: 0,
  sequence: 0,
  isOnSale: true,
  isPointsExchange: false,
  specs: [],
});

const formState = reactive<GoodsEditorFormState>(createDefaultState());

const rules: FormRules = {
  categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
  description: [{ required: true, message: '请输入描述', trigger: 'blur' }],
  image: [{ required: true, message: '请上传图片', trigger: 'change' }],
  price: [
    { required: true, message: '请输入价格', trigger: 'blur' },
    { pattern: /^\d+(?:\.\d{1,2})?$/u, message: '价格格式不正确，最多保留两位小数', trigger: 'blur' },
  ],
};

const sanitizeSpecs = (specs: GoodsSpec[]) => (specs || [])
  .map((item) => ({
    name: (item.name || '').trim(),
    options: (item.options || []).map((option) => (option || '').trim()).filter(Boolean),
  }))
  .filter((item) => item.name && item.options.length > 0)
  .map((item) => ({
    name: item.name,
    options: Array.from(new Set(item.options)),
  }));

const syncFormState = () => {
  const nextState = {
    ...createDefaultState(),
    ...(props.initialValue || {}),
    isPointsExchange: Boolean(props.initialValue?.isPointsExchange),
    specs: sanitizeSpecs((props.initialValue?.specs || []) as GoodsSpec[]),
  };
  Object.assign(formState, nextState);
};

watch(() => props.visible, (visible) => {
  if (visible) {
    syncFormState();
  }
}, { immediate: true });

const normalizedUploadUrl = computed(() => {
  if (!props.uploadUrl) {
    return props.baseUrl;
  }
  if (props.uploadUrl.startsWith('http')) {
    return props.uploadUrl;
  }
  if (props.uploadUrl.startsWith(`${props.baseUrl}/`) || props.uploadUrl === props.baseUrl) {
    return props.uploadUrl;
  }
  return `${props.baseUrl}${props.uploadUrl}`;
});

const getFullUrl = (url?: string) => {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return props.baseUrl + url;
};

const handleUploadSuccess = (response: any) => {
  const imageUrl = response?.data?.[0];
  if (imageUrl) {
    formState.image = imageUrl;
  }
};

const handleClose = () => {
  emit('update:visible', false);
};

const handleSubmit = async() => {
  await formRef.value?.validate();
  const specs = sanitizeSpecs(formState.specs);
  const hasInvalidSpec = (formState.specs || []).some((item) => {
    const groupName = (item.name || '').trim();
    const options = (item.options || []).map((option) => (option || '').trim()).filter(Boolean);
    return !groupName || options.length === 0;
  });
  if (hasInvalidSpec) {
    ElMessage.error('请完善规格组名称和至少一个规格值');
    return;
  }
  emit('submit', {
    ...formState,
    specs,
  });
};

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_upload = _resolveComponent("el-upload")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    "model-value": __props.visible,
    title: __props.mode === 'create' ? '创建商品' : '修改商品',
    width: "720px",
    "append-to-body": "",
    "close-on-click-modal": false,
    onClose: handleClose
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, { onClick: handleClose }, {
        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
          _createTextVNode("取消", -1)
        ]))]),
        _: 1
      }),
      _createVNode(_component_el_button, {
        type: "primary",
        loading: __props.submitting,
        onClick: handleSubmit
      }, {
        default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
          _createTextVNode("确定", -1)
        ]))]),
        _: 1
      }, 8, ["loading"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_el_form, {
        ref_key: "formRef",
        ref: formRef,
        model: formState,
        rules: rules,
        "label-width": "96px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_form_item, {
            label: "分类",
            prop: "categoryId"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_select, {
                modelValue: formState.categoryId,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.categoryId) = $event)),
                placeholder: "请选择分类",
                style: {"width":"100%"}
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.categoryOptions, (item) => {
                    return (_openBlock(), _createBlock(_component_el_option, {
                      key: item.value,
                      label: item.label,
                      value: item.value
                    }, null, 8, ["label", "value"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_form_item, {
            label: "名称",
            prop: "name"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: formState.name,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.name) = $event)),
                placeholder: "请输入商品名称"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_form_item, {
            label: "描述",
            prop: "description"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: formState.description,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.description) = $event)),
                type: "textarea",
                rows: 3,
                placeholder: "请输入商品描述"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_form_item, {
            label: "图片",
            prop: "image"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                (formState.image)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createVNode(_component_el_image, {
                        src: getFullUrl(formState.image),
                        fit: "cover"
                      }, null, 8, ["src"])
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_upload, {
                  action: normalizedUploadUrl.value,
                  headers: __props.uploadHeaders,
                  name: "files",
                  "show-file-list": false,
                  accept: "image/*",
                  "on-success": handleUploadSuccess
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      plain: ""
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode("上传图片", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["action", "headers"])
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_row, { gutter: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, {
                    label: "价格",
                    prop: "price"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input, {
                        modelValue: formState.price,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formState.price) = $event)),
                        placeholder: "请输入价格"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, {
                    label: "赠送积分",
                    prop: "giftPoints"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input_number, {
                        modelValue: formState.giftPoints,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formState.giftPoints) = $event)),
                        min: 0,
                        precision: 0,
                        style: {"width":"100%"}
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_row, { gutter: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, {
                    label: "库存",
                    prop: "stock"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input_number, {
                        modelValue: formState.stock,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formState.stock) = $event)),
                        min: 0,
                        precision: 0,
                        style: {"width":"100%"}
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, {
                    label: "排序",
                    prop: "sequence"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input_number, {
                        modelValue: formState.sequence,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formState.sequence) = $event)),
                        min: 0,
                        precision: 0,
                        style: {"width":"100%"}
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_row, { gutter: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, { label: "上架状态" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_switch, {
                        modelValue: formState.isOnSale,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formState.isOnSale) = $event))
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_el_col, { span: 12 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, { label: "积分兑换" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_switch, {
                        modelValue: formState.isPointsExchange,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formState.isPointsExchange) = $event))
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_form_item, { label: "规格配置" }, {
            default: _withCtx(() => [
              _createVNode(GoodsSpecEditor, {
                modelValue: formState.specs,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formState.specs) = $event))
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["model"])
    ]),
    _: 1
  }, 8, ["model-value", "title"]))
}
}

})